import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { GeoJSON, MapContainer, TileLayer, useMap } from 'react-leaflet';
import L, { latLngBounds, LatLngBounds } from 'leaflet';
import html2canvas from 'html2canvas';

export type VisualizeMapStyle = 'light' | 'dark' | 'satellite';

export type VisualizeFeatureCollection = {
  type: 'FeatureCollection';
  meta?: {
    area_codes?: string[];
    [key: string]: unknown;
  };
  features: Array<{
    type: 'Feature';
    geometry: {
      type: string;
      coordinates: any;
    } | null;
    properties?: Record<string, unknown>;
  }>;
};

type ModalProps = {
  show: boolean;
  title: string;
  data: VisualizeFeatureCollection | null;
  mapStyle: VisualizeMapStyle;
  cityCounts?: Array<{ code: string; name: string; count: number }>;
  onMapStyleChange: (style: VisualizeMapStyle) => void;
  onClose: () => void;
};

type SnapshotProps = {
  active: boolean;
  title: string;
  filename: string;
  data: VisualizeFeatureCollection | null;
  mapStyle: VisualizeMapStyle;
  cityCounts?: Array<{ code: string; name: string; count: number }>;
  onDone: () => void;
};

const SNAPSHOT_WIDTH_PX = 1920;
const SNAPSHOT_HEIGHT_PX = 1200;
const SNAPSHOT_MAP_HEIGHT_PX = 1080;
const SNAPSHOT_SCALE = Math.max(2, Math.min(3, Math.ceil((window.devicePixelRatio || 1) * 2)));

const defaultCenter: [number, number] = [33.8938, 35.5018];

const waitForIdle = () =>
  new Promise<void>((resolve) => {
    const idle = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number }).requestIdleCallback;
    if (typeof idle === 'function') {
      idle(() => resolve(), { timeout: 500 });
      return;
    }
    window.setTimeout(() => resolve(), 32);
  });

const collectLatLng = (data: VisualizeFeatureCollection | null): Array<[number, number]> => {
  if (!data) return [];
  const all: Array<[number, number]> = [];

  const walkCoords = (coords: any) => {
    if (!Array.isArray(coords)) return;
    if (coords.length >= 2 && typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      all.push([coords[1], coords[0]]);
      return;
    }
    coords.forEach(walkCoords);
  };

  data.features.forEach((feature) => {
    if (!feature.geometry) return;
    walkCoords(feature.geometry.coordinates);
  });

  return all;
};

const readNumeric = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const FEATURE_VALUE_KEYS = ['population_count', 'population', 'value', 'density', 'count', 'total', 'amount'];

const getCongestionColor = (congestionRaw: unknown): string | null => {
  if (typeof congestionRaw !== 'string') return null;
  const congestion = congestionRaw.toUpperCase();
  if (congestion === 'HEAVY') return '#dc2626';
  if (congestion === 'MODERATE' || congestion === 'SLOW') return '#f59e0b';
  if (congestion === 'FREE_FLOW') return '#16a34a';
  return null;
};

const getFeatureIntensity = (
  feature: { properties?: Record<string, unknown> } | null | undefined,
  minValue: number,
  maxValue: number
): number => {
  if (!feature?.properties || maxValue <= minValue) return 0.5;
  for (const key of FEATURE_VALUE_KEYS) {
    const raw = feature.properties[key];
    const val = readNumeric(raw);
    if (val != null) {
      const norm = (val - minValue) / (maxValue - minValue);
      return Math.max(0, Math.min(1, norm));
    }
  }
  return 0.45;
};

const colorRamp = (intensity: number): string => {
  const stops = ['#86efac', '#4ade80', '#22c55e', '#16a34a', '#166534'];
  const idx = Math.min(stops.length - 1, Math.max(0, Math.floor(intensity * stops.length)));
  return stops[idx];
};

const getValueBounds = (data: VisualizeFeatureCollection | null): { min: number; max: number } => {
  if (!data) return { min: 0, max: 1 };
  const nums: number[] = [];
  data.features.forEach((feature) => {
    if (!feature.properties) return;
    for (const key of FEATURE_VALUE_KEYS) {
      const val = readNumeric(feature.properties[key]);
      if (val != null) {
        nums.push(val);
        break;
      }
    }
  });
  if (nums.length === 0) return { min: 0, max: 1 };
  return { min: Math.min(...nums), max: Math.max(...nums) };
};

const getMapTileConfig = (mapStyle: VisualizeMapStyle): { url: string; attribution: string; labelsOverlay?: { url: string; attribution: string } } => {
  if (mapStyle === 'dark') {
    return {
      url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
      attribution: '&copy; OpenStreetMap contributors &copy; CARTO'
    };
  }
  if (mapStyle === 'satellite') {
    return {
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      attribution: 'Tiles &copy; Esri',
      labelsOverlay: {
        url: 'https://services.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
        attribution: 'Labels &copy; Esri'
      }
    };
  }
  return {
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
  };
};

const FitMapOnOpen: React.FC<{ bounds: LatLngBounds | null }> = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    const t1 = window.setTimeout(() => map.invalidateSize(true), 0);
    const t2 = window.setTimeout(() => map.invalidateSize(true), 300);
    const t3 = window.setTimeout(() => {
      map.invalidateSize(true);
      if (bounds) {
        map.fitBounds(bounds, { padding: [32, 32], maxZoom: 14 });
      }
    }, 900);

    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
      window.clearTimeout(t3);
    };
  }, [map, bounds]);

  return null;
};

const CaptureMapStabilizer: React.FC<{
  enabled: boolean;
  bounds: LatLngBounds | null;
  onStable?: () => void;
}> = ({ enabled, bounds, onStable }) => {
  const map = useMap();

  useEffect(() => {
    if (!enabled || !onStable) return;
    let cancelled = false;
    const markStable = () => {
      if (cancelled) return;
      onStable();
    };

    const t = window.setTimeout(() => {
      map.invalidateSize(true);
      if (bounds) {
        map.fitBounds(bounds, { padding: [32, 32], maxZoom: 14, animate: false, noMoveStart: true });
      }
    }, 80);

    map.once('idle', markStable);

    const fallback = window.setTimeout(() => {
      markStable();
    }, 2600);

    return () => {
      cancelled = true;
      window.clearTimeout(t);
      window.clearTimeout(fallback);
      map.off('idle', markStable);
    };
  }, [enabled, bounds, map, onStable]);

  return null;
};

const RenderMap: React.FC<{
  data: VisualizeFeatureCollection | null;
  mapStyle: VisualizeMapStyle;
  className?: string;
  titleOverlay?: string;
  cityCounts?: Array<{ code: string; name: string; count: number }>;
  captureMode?: boolean;
  showZoomControl?: boolean;
  onCaptureStable?: () => void;
}> = ({
  data,
  mapStyle,
  className = 'h-full w-full',
  titleOverlay,
  cityCounts,
  captureMode = false,
  showZoomControl = true,
  onCaptureStable
}) => {
  const points = useMemo(() => collectLatLng(data), [data]);
  const canvasRenderer = useMemo(() => L.canvas({ padding: 0.5 }), []);
  const bounds = useMemo(() => {
    if (points.length === 0) return null;
    return latLngBounds(points);
  }, [points]);

  const valueBounds = useMemo(() => getValueBounds(data), [data]);
  const tile = useMemo(() => getMapTileConfig(mapStyle), [mapStyle]);

  return (
    <div className={`relative ${className}`}>
      {titleOverlay && (
        <div className="pointer-events-none absolute left-4 top-4 z-[1000] rounded-lg bg-white/95 px-3 py-2 text-left text-sm font-bold text-slate-900 shadow-md ring-1 ring-slate-200">
          {titleOverlay}
        </div>
      )}
      {cityCounts && cityCounts.length > 0 && (
        <div className="pointer-events-none absolute right-4 top-4 z-[1000] rounded-lg bg-white/95 px-3 py-2 text-left text-xs font-semibold text-slate-800 shadow-md ring-1 ring-slate-200">
          <div className="pb-1 text-[11px] uppercase tracking-wide text-slate-500">Records by city</div>
          {cityCounts.map((city) => (
            <div key={city.code} className="flex min-w-[180px] items-center justify-between gap-3">
              <span>{city.name}</span>
              <span className="font-bold text-slate-900">{city.count}</span>
            </div>
          ))}
        </div>
      )}
      <MapContainer
        key={`visual-map-${mapStyle}-${data?.features?.length || 0}`}
        center={defaultCenter}
        zoom={8}
        className="h-full w-full"
        preferCanvas
        zoomAnimation={!captureMode}
        fadeAnimation={!captureMode}
        markerZoomAnimation={!captureMode}
        bounds={bounds || undefined}
        scrollWheelZoom
        zoomControl={showZoomControl}
      >
        {captureMode
          ? <CaptureMapStabilizer enabled={captureMode} bounds={bounds} onStable={onCaptureStable} />
          : <FitMapOnOpen bounds={bounds} />}
        <TileLayer
          attribution={tile.attribution}
          url={tile.url}
          crossOrigin="anonymous"
        />
        {tile.labelsOverlay && (
          <TileLayer
            attribution={tile.labelsOverlay.attribution}
            url={tile.labelsOverlay.url}
            crossOrigin="anonymous"
          />
        )}
        {data && (
          <GeoJSON
            data={data as any}
            pointToLayer={(feature, latlng) => {
              const congestionColor = getCongestionColor((feature as any)?.properties?.congestion);
              const intensity = getFeatureIntensity(feature as any, valueBounds.min, valueBounds.max);
              const color = congestionColor || colorRamp(intensity);
              return L.circleMarker(latlng, {
                radius: 3,
                color,
                weight: 0.8,
                fillOpacity: 0.8,
                renderer: canvasRenderer
              });
            }}
            style={(feature) => {
              const congestionColor = getCongestionColor((feature as any)?.properties?.congestion);
              const intensity = getFeatureIntensity(feature as any, valueBounds.min, valueBounds.max);
              const color = congestionColor || colorRamp(intensity);
              return {
                color,
                weight: 1.2,
                fillColor: color,
                fillOpacity: 0.12,
                opacity: 0.85,
                renderer: canvasRenderer
              };
            }}
          />
        )}
      </MapContainer>
    </div>
  );
};

export const ReportVisualizeModal: React.FC<ModalProps> = ({
  show,
  title,
  data,
  mapStyle,
  cityCounts,
  onMapStyleChange,
  onClose
}) => {
  return (
    <Transition show={show} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-[9999]">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl overflow-hidden rounded-2xl bg-white p-4 shadow-2xl ring-1 ring-slate-100">
                <div className="flex items-center justify-between gap-2 pb-3">
                  <Dialog.Title className="text-lg font-semibold text-slate-900">{title}</Dialog.Title>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 rounded-full bg-white/90 p-1 text-xs font-semibold text-slate-700 shadow ring-1 ring-slate-200">
                      {(['light', 'dark', 'satellite'] as const).map((style) => (
                        <button
                          key={style}
                          className={`rounded-full px-3 py-1 transition ${mapStyle === style ? 'bg-slate-900 text-white shadow' : 'text-slate-700 hover:bg-slate-100'}`}
                          onClick={() => onMapStyleChange(style)}
                        >
                          {style === 'light' ? 'Light' : style === 'dark' ? 'Dark' : 'Satellite'}
                        </button>
                      ))}
                    </div>
                    <button
                      onClick={onClose}
                      className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-700"
                    >
                      Close
                    </button>
                  </div>
                </div>
                <div className="h-[70vh] overflow-hidden rounded-xl border border-slate-200">
                  <RenderMap data={data} mapStyle={mapStyle} cityCounts={cityCounts} />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export const ReportVisualizeSnapshotCapture: React.FC<SnapshotProps> = ({
  active,
  title,
  filename,
  data,
  mapStyle,
  cityCounts,
  onDone
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [captureStable, setCaptureStable] = useState(false);

  useEffect(() => {
    if (!active) return;
    setCaptureStable(false);
  }, [active, data, mapStyle]);

  useEffect(() => {
    if (!active || !panelRef.current || !data || !captureStable) return;

    const timer = window.setTimeout(async () => {
      try {
        await waitForIdle();
        await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
        await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
        const canvas = await html2canvas(panelRef.current as HTMLDivElement, {
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
          scale: SNAPSHOT_SCALE,
          onclone: (doc) => {
            const node = doc.getElementById('visualize-snapshot-root');
            if (node) {
              node.style.opacity = '1';
            }
          }
        });
        const blob: Blob | null = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (!blob) return;
        canvas.remove();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename.endsWith('.png') ? filename : `${filename}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } finally {
        onDone();
      }
    }, 250);

    return () => window.clearTimeout(timer);
  }, [active, captureStable, data, filename, mapStyle, onDone]);

  if (!active || !data) return null;

  return (
    <div
      id="visualize-snapshot-root"
      className="pointer-events-none fixed left-0 top-0 z-[-1] overflow-hidden bg-white opacity-0"
      style={{ width: `${SNAPSHOT_WIDTH_PX}px`, height: `${SNAPSHOT_HEIGHT_PX}px` }}
      aria-hidden
    >
      <div ref={panelRef} className="h-full w-full bg-white p-4">
        <div className="pb-2 text-xl font-semibold text-slate-900">{title}</div>
        <div className="overflow-hidden rounded-xl border border-slate-200" style={{ height: `${SNAPSHOT_MAP_HEIGHT_PX}px` }}>
          <RenderMap
            data={data}
            mapStyle={mapStyle}
            titleOverlay={`Records: ${data.features.length}`}
            cityCounts={cityCounts}
            captureMode
            showZoomControl={false}
            onCaptureStable={() => setCaptureStable(true)}
          />
        </div>
      </div>
    </div>
  );
};
